<template>
    <div>
        <form-wrapper :validator="$v.chart" class="text-14">
            <h4 class="pb-8 font-bold text-14 pt-14">Box Title</h4>
            <form-group name="title" class="mb-14">
                <input
                    slot-scope="{attributes}"
                    v-model="chart.title"
                    @input="delayTouch($v.chart.title)"
                    v-bind="attributes"
                    type="text"
                    placeholder="Box title..."
                    class="w-full"
                >
            </form-group>

            <h4 class="pb-8 font-bold text-14 pt-14">Data Source</h4>
            <form-group name="data_source" class="mb-14">
                <textarea
                    slot-scope="{attributes}"
                    v-model="chart.data_source"
                    @input="onDataSourceInput"
                    v-bind="attributes"
                    placeholder="Data source..."
                    class="font-mono text-12 h-86"
                ></textarea>
            </form-group>

            <div class="flex justify-start w-full">
                <div class="w-1/2 mr-8">
                    <h4 class="pb-8 font-bold text-14 pt-14">Category Tags</h4>
                    <form-group name="tags.category" class="mb-14">
                        <div class="w-full">
                            <multiselect
                                label="name"
                                placeholder="Start typing..."
                                tag-placeholder=""
                                track-by="name"
                                v-model="chart.tags.category"
                                :multiple="true"
                                :options="categoryTags"
                                :show-labels="false"
                                :taggable="true"
                                @tag="addCategoryTag"
                            ></multiselect>
                        </div>
                    </form-group>
                </div>
                <div class="w-1/2 ml-8">
                    <h4 class="pb-8 font-bold text-14 pt-14">Channel Tags</h4>
                    <form-group name="tags.channel" class="mb-14">
                        <div class="w-full">
                            <multiselect
                                label="name"
                                placeholder="Start typing..."
                                tag-placeholder=""
                                track-by="name"
                                v-model="chart.tags.channel"
                                :multiple="true"
                                :options="channelTags"
                                :show-labels="false"
                                :taggable="true"
                                @tag="addChannelTag"
                            ></multiselect>
                        </div>
                    </form-group>
                </div>
            </div>

            <template v-if="hasData">
                <h4 class="font-bold text-14 py-14">Chart Type</h4>
                <form-group name="type" label="Format" class="mb-14">
                    <div class="w-2/3 ml-auto">
                        <multiselect
                            :options="filteredChartTypes"
                            :searchable="false"
                            :show-labels="false"
                            @input="onChartType"
                            label="name"
                            placeholder="Select chart type..."
                            track-by="id"
                            v-model="chart.type"
                        ></multiselect>
                    </div>
                </form-group>

                <template v-if="hasCompatibleMetrics">
                    <form-group name="type" label="Number rounding" class="mb-14">
                        <div class="flex w-2/3 ml-auto">
                            <span
                                @click="setNumberRounding(true)"
                                :class="{'bg-pink border-pink': isNumberRounded, 'border-grey': !isNumberRounded}"
                                class="flex-1 h-40 text-center border border-r-0 rounded-l-8 px-18 leading-40"
                            >1,234</span>
                            <span
                                @click="setNumberRounding(false)"
                                :class="{'bg-pink border-pink': !isNumberRounded, 'border-grey': isNumberRounded}"
                                class="flex-1 h-40 text-center border border-l-0 rounded-r-8 px-18 leading-40"
                            >1,234.56</span>
                        </div>
                    </form-group>

                    <form-group v-if="hasChartType" name="dimension" label="Dimension" class="mb-14">
                        <div class="w-2/3 ml-auto">
                            <multiselect
                                :allow-empty="false"
                                :options="chart.type.dimensions"
                                :searchable="false"
                                :show-labels="false"
                                label="name"
                                placeholder="Select dimension..."
                                track-by="name"
                                v-model="chart.dimension"
                            ></multiselect>
                        </div>
                    </form-group>

                    <form-group v-if="isSingleDimensionChartType" name="invert" label="Invert" class="mb-14">
                        <div class="flex w-2/3 ml-auto">
                            <span
                                @click="setInverted(true)"
                                :class="{'bg-pink border-pink': chart.is_inverted, 'border-grey': !chart.is_inverted}"
                                class="flex-1 h-40 text-center border border-r-0 rounded-l-8 px-18 leading-40"
                            >True</span>
                            <span
                                @click="setInverted(false)"
                                :class="{'bg-pink border-pink': !chart.is_inverted, 'border-grey': chart.is_inverted}"
                                class="flex-1 h-40 text-center border border-l-0 rounded-r-8 px-18 leading-40"
                            >False</span>
                        </div>
                    </form-group>

                    <div v-if="hasMetrics && !isSplitColumnBy" class="mb-14">
                        <h4 class="font-bold text-14 py-14">Metrics</h4>
                        <div v-for="(metric, index) in $v.chart.metrics.$each.$iter" :key="index">
                            <form-group :name="`metrics.$each.${index}.field`" :label="`Metric ${parseInt(index) + 1}`" class="mb-4">
                                <div class="w-2/3">
                                    <multiselect
                                        v-model="chart.metrics[index]"
                                        :options="metricOptions"
                                        :searchable="false"
                                        :show-labels="false"
                                        @input="onMetricSelect"
                                        :placeholder="`Select metric ${parseInt(index) + 1}...`"
                                        label="field"
                                    >
                                    </multiselect>
                                </div>
                            </form-group>
                            <form-group :name="`metrics.$each.${index}.label`" label="Name" class="mb-4">
                                <input
                                    slot-scope="{attributes}"
                                    v-model="metric.$model.label"
                                    @input="delayTouch(metric.label)"
                                    v-bind="attributes"
                                    type="text"
                                    :placeholder="`Metric ${parseInt(index) + 1} name...`"
                                    class="max-w-2/3"
                                >
                            </form-group>
                            <div @click="onMetricDelete(index)" class="flex justify-end mb-14">
                                <a class="flex items-center group">
                                    <svg class="mr-4 -mt-2 w-14 h-14 fill-blue-lightest group-hover:fill-coral" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg"><path d="M704 1376v-704q0-14-9-23t-23-9h-64q-14 0-23 9t-9 23v704q0 14 9 23t23 9h64q14 0 23-9t9-23zm256 0v-704q0-14-9-23t-23-9h-64q-14 0-23 9t-9 23v704q0 14 9 23t23 9h64q14 0 23-9t9-23zm256 0v-704q0-14-9-23t-23-9h-64q-14 0-23 9t-9 23v704q0 14 9 23t23 9h64q14 0 23-9t9-23zm-544-992h448l-48-117q-7-9-17-11h-317q-10 2-17 11zm928 32v64q0 14-9 23t-23 9h-96v948q0 83-47 143.5t-113 60.5h-832q-66 0-113-58.5t-47-141.5v-952h-96q-14 0-23-9t-9-23v-64q0-14 9-23t23-9h309l70-167q15-37 54-63t79-26h320q40 0 79 26t54 63l70 167h309q14 0 23 9t9 23z"/></svg>
                                    <span class="text-blue-lightest group-hover:text-coral">Remove this metric</span>
                                </a>
                            </div>
                        </div>

                        <div v-if="canAddMetric" class="flex justify-end">
                            <span @click="addEmptyMetric" class="mt-4 cursor-pointer">+ add another</span>
                        </div>
                    </div>
                </template>
                <div v-else class="mb-14">
                    Selected chart format incompatible with data source metrics
                </div>
            </template>

            <div v-if="errors" class="flex flex-col p-8 my-14 bg-coral-darkest">
                <span v-for="error in errors" :key="error" class="mb-2 text-white">
                    {{error[0]}}
                </span>
            </div>

            <div class="flex justify-end w-full pt-15 pb-30">
                <button @click="onCancel" class="mr-4 bg-blue hover:bg-blue-dark">Cancel</button>
                <button @click="onCreate" :disabled="creating" class="ml-4">
                    <span v-if="creating">Saving...</span>
                    <span v-else>Save</span>
                </button>
            </div>

        </form-wrapper>
    </div>
</template>

<script>
    import adminMixin from './mixins/admin'
    import { between, email, integer, maxLength, minLength, required, requiredIf, sameAs, url } from "vuelidate/lib/validators"
    import { call, get, sync } from 'vuex-pathify'
    import Multiselect from "vue-multiselect"
    import validationMixin from "../../../mixins/validation"

    export default {
        components: {
            Multiselect,
        },

        mixins: [validationMixin, adminMixin],

        data() {
            return {
                filteredDatasets: [],
            }
        },

        computed: {
            chart: sync('charts/chart'),
            creating: get('charts/creating'),
        },

        mounted() {
            this.loadChartTypes(false)
        },

        methods: {
            create: call('charts/create'),
            resetChart: call('charts/resetChart'),

            close() {
                this.$v.$reset()
                this.resetChart()
                this.errors = null
            },

            async onCreate() {
                this.$v.$touch()

                if (!this.$v.$invalid) {
                    try {
                        await this.create(this.chart)

                        this.close()
                        this.$emit('created')
                    } catch (error) {
                        this.errors = error.response.data.errors

                        this.$v.$reset()
                    }
                }
            },
        },

        beforeDestroy() {
            this.close()
        },

        validations: {
            chart: {
                title: {
                    required,
                    minLength: minLength(3),
                    maxLength: maxLength(36),
                },
                data_source: {
                    minLength: minLength(3),
                    maxLength: maxLength(1000),
                    required,
                    url,
                },
                dimension: {
                    required,
                },
                metrics: {
                    $each: {
                        field: {
                            required,
                        },
                        label: {
                            minLength: minLength(3),
                            maxLength: maxLength(50),
                        },
                    },
                }
            }
        },
    }
</script>
